import React, { useState } from "react";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import PreviewModal from "./PreviewModal";
import DocumentViewer from "../employeesNew/DocumentViewer";

const DocumentUploadDisplay = ({
  values,
  frontDocKey,
  backDocKey,
  handleRemoveDocument,
  setFieldValue,
  showSize = true,
  currentEmployeeEditing = false,
  onDocModal,
  errors,
  touched,
  customStyle = {},
  uploadButtonText = "Upload",
  headerText = "Upload Document",
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUrl, setModalUrl] = useState("");

  const frontDoc = values?.[frontDocKey];
  const backDoc = values?.[backDocKey];

  const isFile = (doc) => doc instanceof File;
  const isLink = (doc) => typeof doc === "string";

  const openModal = (url) => {
    setModalUrl(url);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalUrl("");
  };

  const renderDocumentInfo = (doc, docType) => {
    if (isFile(doc)) {
      // File object - show file details
      return (
        <>
          <span className="file-name">
            {t(`${docType} Document:`)} {doc.name}
          </span>
          {showSize && (
            <span className="file-size">
              {t("file_size_label", { size: (doc.size / 1024).toFixed(2) })}
            </span>
          )}
        </>
      );
    } else if (isLink(doc)) {
      // String link - show view option
      return (
        <span className="file-name">
          {t(`${docType} Document:`)}{" "}
          <button
            type="button"
            onClick={() => openModal(doc)}
            className="bg-blue-100 text-blue-700 px-4 py-2 rounded-md hover:bg-blue-200 text-sm transition"
          >
            {t("View")}
          </button>
        </span>
      );
    }
    return null;
  };

  const renderActionButton = (doc, docKey, docType) => {
    if (currentEmployeeEditing) {
      return null; // Hide buttons in edit mode
    }

    if (isFile(doc)) {
      return (
        <button
          type="button"
          className="remove-file-btn"
          onClick={() =>
            handleRemoveDocument(docKey, docType.toLowerCase(), setFieldValue)
          }
        >
          {t("Remove")}
        </button>
      );
    } else if (isLink(doc)) {
      return (
        <button
          type="button"
          className="remove-file-btn"
          onClick={() =>
            handleRemoveDocument(docKey, docType.toLowerCase(), setFieldValue)
          }
        >
          {t("Remove")}
        </button>
      );
    }
    return null;
  };

  return (
    <>
      <div className="add-upload-doc-container" style={customStyle}>
        <div className="add-upload-header">
          <h2 className="add-upload-heading">{t(headerText)}</h2>
          <button
            type="button"
            className="add-upload-btn"
            onClick={onDocModal}
            disabled={currentEmployeeEditing}
            style={{
              opacity: currentEmployeeEditing ? 0.5 : 1,
              cursor: currentEmployeeEditing ? "not-allowed" : "pointer",
              pointerEvents: currentEmployeeEditing ? "none" : "auto",
            }}
          >
            {currentEmployeeEditing
              ? t("Upload Restricted")
              : t(uploadButtonText)}
          </button>
        </div>

        {/* Error Messages */}
        {errors && touched && (
          <>
            <CustomErrorMessage
              name={frontDocKey}
              errors={errors}
              touched={touched}
            />
            <CustomErrorMessage
              name={backDocKey}
              errors={errors}
              touched={touched}
            />
          </>
        )}

        {/* Document Display */}
        <div className="add-display-upload">
          <div>
            {/* Front Document Details */}
            {frontDoc && (
              <div className="uploaded-file-details">
                <div className="file-info">
                  {renderDocumentInfo(frontDoc, "Front")}
                </div>
                {renderActionButton(frontDoc, frontDocKey, "Front")}
              </div>
            )}

            {/* Back Document Details */}
            {backDoc && (
              <div className="uploaded-file-details">
                <div className="file-info">
                  {renderDocumentInfo(backDoc, "Back")}
                </div>
                {renderActionButton(backDoc, backDocKey, "Back")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for viewing documents */}
      {modalOpen && (
        <DocumentViewer
          documentUrl={modalUrl}
          isOpen={modalOpen}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default DocumentUploadDisplay;
